import React, { useState } from 'react';
import { RestaurantInfoModalReservation_getRestaurantDetails } from '../../../../../types/graphql-types-generated.ts';
import { Button } from '../../../../../patterns/00-atoms/button/button.tsx';
import { Datepicker } from '../../../../../patterns/00-atoms/datepicker/datepicker.tsx';
import { Dropdown } from '../../../../../patterns/00-atoms/dropdown/dropdown.tsx';
import { Reservation } from '../../../../../utils/reservation.ts';
import { PhoneLink } from '../../../../../patterns/00-atoms/phone/phone-link.tsx';
import { createGraphQLService } from '../../../../../../../server/source/services/endpoint-factory.ts';
import { AppContext, useAppContext } from '../../../../../components/app-context/app-context.tsx';
import {
  openTableAvailabilityCheck,
  saveTableBooking
} from '../../03-templates/restaurant-detail-page/reservation.graphql.ts';
import { Skeleton } from '../../../../../patterns/00-atoms/cn/skeleton.tsx';
import { format } from 'date-fns';
import { Label } from '../../../../../patterns/00-atoms/cn/label.tsx';
import { Checkbox } from '../../../../../patterns/00-atoms/checkbox/checkbox.tsx';
import { Textarea } from '../../../../../patterns/00-atoms/textarea/textarea.tsx';
import { Input } from '../../../../../patterns/00-atoms/input/input.tsx';
import { classList, pluralize } from '../../../../../utils/string.ts';
import { safeWindow } from '../../../../../utils/window.ts';

export type RestaurantInfoModalReservationProps = {
  details: RestaurantInfoModalReservation_getRestaurantDetails;
};

export const RestaurantInfoModalReservation: React.FunctionComponent<
  RestaurantInfoModalReservationProps
> = ({ details }) => {
  const { appContext } = useAppContext();
  const reservation = new Reservation(details);

  return (
    <div className="text-sm">
      <h2 className="mt-3 text-xl font-extrabold">Reservieren</h2>
      {reservation.activePartner === reservation.partner.LIVEBOOKING &&
        details.partnerSettingsLivebooking?.widgetUrl && (
          <Livebooking widgetUrl={details.partnerSettingsLivebooking.widgetUrl} />
        )}
      {reservation.activePartner === reservation.partner.OPENTABLE && (
        <OpenTable details={details} appContext={appContext} />
      )}
      {reservation.activePartner === reservation.partner.QUANDOO &&
        details.partnerSettingsQuandoo?.partnerId && (
          <Quandoo quandooId={details.partnerSettingsQuandoo.partnerId} />
        )}
      {reservation.activePartner === reservation.partner.SPEISEKARTE && (
        <SpeisekarteTableBooking details={details} appContext={appContext} />
      )}
    </div>
  );
};

type OpenTableProps = {
  details: RestaurantInfoModalReservation_getRestaurantDetails;
  appContext: AppContext;
};

type OpenTableAvailabilityResult = {
  availableTimes: Array<{ time: string; bookingUrl: string }>;
};

const OpenTable: React.FC<OpenTableProps> = ({ details, appContext }) => {
  const reservationCalendarUrl = 'http://www.opentable.de/single.aspx?rid=349884&ref=11434';
  const restaurantId = Number(details.id);
  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i.toString(),
    label: i.toString().padStart(2, '0')
  }));
  const minuteOptions = ['00', '15', '30', '45'].map(minute => ({
    value: minute,
    label: minute
  }));
  const personsOptions = Array.from({ length: 20 }, (_, i) => i + 1).map(i => ({
    value: i.toString(),
    label: `${i} ${i === 1 ? 'Person' : 'Personen'}`
  }));

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [result, setResult] = React.useState<OpenTableAvailabilityResult>({ availableTimes: [] });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    setLoading(true);
    setError(false);
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const date = formData.get('openTable[date]') as string;
    const hour = formData.get('openTable[time][hour]') as string;
    const minute = formData.get('openTable[time][minute]') as string;
    const time = `${hour}:${minute}`;
    const partySize = Number(formData.get('openTable[partySize]'));

    appContext.graphqlService = createGraphQLService(appContext, 'public');

    openTableAvailabilityCheck({ restaurantId, date, time, partySize }, appContext)
      .then(response => {
        setLoading(false);
        const availableTimes = response.data?.checkOpenTableAvailability?.availableTimes;
        if (availableTimes && availableTimes?.length > 0) {
          setResult({
            availableTimes: availableTimes.map(time => ({
              time: time?.time?.toString() || '',
              bookingUrl: time?.bookingUrl || ''
            }))
          });
        } else {
          setResult({ availableTimes: [] });
          setError(true);
        }
      })
      .catch(() => {
        console.error('OpenTable availability check failed', {
          restaurantId,
          date,
          time,
          partySize
        });
        setLoading(false);
        setError(true);
      });
  };

  return (
    <>
      <p className="mt-2">
        Noch Tische frei zu Deinem Wunschtermin? <br />
        Prüfe hier die Verfügbarkeit
      </p>
      <form method="post" onSubmit={handleSubmit}>
        <div className="flex flex-col">
          <Datepicker
            name="openTable[date]"
            className="mt-4"
            value={new Date().toISOString().split('T')[0]}
          />
          <div className="mt-2 flex">
            <Dropdown
              options={hourOptions}
              name="openTable[time][hour]"
              containerClassName="flex-grow"
            />
            <div className="p-2">:</div>
            <Dropdown
              options={minuteOptions}
              name="openTable[time][minute]"
              containerClassName="flex-grow"
            />
          </div>
          <div className="mt-2">
            <Dropdown options={personsOptions} name="openTable[partySize]" />
          </div>
        </div>

        <div className="mt-4">
          <Button className="w-full text-center" type="submit">
            Freie Tische anzeigen
          </Button>
        </div>
      </form>
      {loading && (
        <div className="mt-4 flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full rounded-full" />
          ))}
        </div>
      )}
      {error && (
        <p className="mt-4 text-red-500">
          Leider war für den angegebenen Zeitraum kein freier Tisch mehr zu finden. Möchtest Du an
          einem anderen Tag oder zu einer anderen Uhrzeit reservieren?
        </p>
      )}
      {result.availableTimes.length > 0 && !loading && (
        <div className="mt-4 flex flex-col gap-2">
          {result.availableTimes.map((time: any, index: number) => (
            <Button
              key={index}
              className="w-full text-center"
              variant="light"
              href={time.bookingUrl}
            >
              {format(new Date(time.time), 'HH:mm')} Uhr reservieren
            </Button>
          ))}
        </div>
      )}
      <div className="mt-4">
        <Button className="w-full text-center" variant="light" href={reservationCalendarUrl}>
          Vollständigen Reservierungs&shy;kalender anzeigen
        </Button>
      </div>
      <div className="mt-8 mb-2 flex justify-center">
        <div className="w-32">
          <OpenTableLogo />
        </div>
      </div>
    </>
  );
};

const OpenTableLogo: React.FC = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 900 300">
      <path
        fill="#da3843"
        d="M176.5 49.5C121 49.5 76 94.5 76 150s45 100.5 100.5 100.5S277 205.5 277 150 232 49.5 176.5 49.5zm0 125.6c-14 0-25.1-11.1-25.1-25.1s11.1-25.1 25.1-25.1 25.1 11.1 25.1 25.1-11.7 25.1-25.1 25.1zM0 150c0-14 11.1-25.1 25.1-25.1S50.3 136 50.3 150s-11.1 25.1-25.1 25.1S0 164 0 150"
      />
      <path
        fill="#212120"
        d="M516.6 130.7c15.2 0 26.3 11.1 26.3 26.3v3.5c0 1.8-1.8 2.3-2.9 2.3h-36.8c0 7 5.8 13.4 14 13.4 5.8 0 9.9-2.3 12.3-4.1 1.2-1.2 2.9-1.2 3.5 0l4.7 6.4c1.2 1.2 1.2 2.3 0 3.5-5.3 4.1-12.3 7.6-21.6 7.6-16.9 0-29.2-13.4-29.2-29.8 1.1-15.6 12.8-29.1 29.7-29.1zm10.5 22.8c0-5.8-4.7-9.9-11.1-9.9-7 0-11.7 4.1-12.3 9.9h23.4zm139.1-45h-58.4c-1.2 0-2.3 1.2-2.3 2.3v9.9c0 1.2 1.2 2.3 2.3 2.3h21v63.7c0 1.2 1.2 2.3 2.3 2.3h11.7c1.2 0 2.3-1.2 2.3-2.3v-63.1h21c1.2 0 2.3-1.2 2.3-2.3v-9.9c.8-1.7-.4-2.9-2.2-2.9zm88.3 22.2c-5.8 0-11.7 1.2-14 2.3v-21.6c0-1.2-1.2-2.9-2.9-2.9h-9.9c-1.2 0-2.9 1.2-2.9 2.9v75.4c0 1.8 1.2 2.9 2.9 2.9h5.3c1.8 0 2.9-1.2 2.9-2.9v-4.1s6.4 8.8 19.3 8.8c15.8 0 26.9-13.4 26.9-29.8.4-18.1-10.7-31-27.6-31zm-1.2 45.6c-7.6 0-11.7-4.7-12.9-8.2v-20.5s4.7-3.5 11.7-3.5c9.4 0 14 7.6 14 15.8.7 8.8-4 16.4-12.8 16.4zm97-45.6c15.2 0 26.3 11.1 26.3 26.3v3.5c0 1.8-1.8 2.3-2.9 2.3h-36.8c0 7 5.8 13.4 14 13.4 5.8 0 9.9-2.3 12.3-4.1 1.2-1.2 2.9-1.2 3.5 0l4.7 6.4c1.2 1.2 1.2 2.3 0 3.5-5.3 4.1-12.3 7.6-21.6 7.6-16.9 0-29.2-13.4-29.2-29.8.5-15.6 12.8-29.1 29.7-29.1zm10.5 22.8c0-5.8-4.7-9.9-11.1-9.9-7 0-11.7 4.1-12.3 9.9h23.4zm-409.6-22.8c-11.7 0-18.1 8.2-18.7 8.8v-4.7c0-1.8-1.2-2.9-2.9-2.9h-5.8c-1.8 0-2.9 1.2-2.9 2.9v75.4c0 1.2 1.2 2.9 2.9 2.9h9.9c1.2 0 2.9-1.2 2.9-2.9v-21.6c2.3 1.2 8.2 2.3 14 2.3 16.9 0 28.6-13.4 28.6-29.8-.6-17.5-12.3-30.4-28-30.4zm-3 45.6c-7 0-11.7-3.5-11.7-3.5v-20.5c1.2-3.5 5.3-8.2 12.9-8.2 8.8 0 13.4 7.6 13.4 15.8.1 8.8-5.2 16.4-14.6 16.4zM585 130.7c-11.1 0-16.4 5.8-18.7 8.2v-4.1c0-1.8-1.2-2.9-2.9-2.9H557c-1.2 0-2.9 1.2-2.9 2.9v52c0 1.8 1.2 2.9 2.9 2.9h9.9c2.9 0 3.5-.6 3.5-2.9v-32.7c1.2-4.1 5.3-8.8 12.3-8.8s9.9 4.7 9.9 12.3v29.8c0 1.8 1.2 2.9 2.9 2.9h9.9c1.2 0 2.9-1.2 2.9-2.9v-29.8c.1-14.6-4.6-26.9-23.3-26.9zm104.6 0c-9.4 0-18.1 2.3-19.9 2.9-1.2.6-2.3 1.2-1.8 3.5l1.2 6.4c0 1.8 1.2 2.9 3.5 2.3 3.5-1.2 11.1-2.3 16.4-2.3 5.8 0 8.2 3.5 8.2 10.5 0 0-5.3-1.8-11.1-1.8-14.6 0-22.8 7.6-22.8 18.1 0 12.3 8.2 19.9 19.3 19.9 8.8 0 14.6-4.1 18.1-7v2.9c0 1.8 1.2 2.9 2.9 2.9h5.9c1.8 0 2.9-1.2 2.9-2.9v-31c-1.2-15-5.3-24.4-22.8-24.4zm6.4 42.1c-.6 2.3-5.3 5.8-10.5 5.8-5.3 0-8.2-3.5-8.2-8.2 0-5.3 3.5-8.2 9.9-8.2 4.7 0 8.2 1.8 8.2 1.8v8.8h.6zm-327.8-64.3c-22.8 0-40.9 18.7-40.9 41.5s18.1 40.9 40.9 40.9c23.4 0 41.5-18.1 41.5-40.9s-18.7-41.5-41.5-41.5zm0 15.8c13.4 0 25.1 11.7 25.1 25.1 0 14-11.1 25.1-25.1 25.1s-25.1-11.1-25.1-25.1c0-13.4 11.6-25.1 25.1-25.1zm443 64.9c1.8 0 2.9-1.2 2.9-2.9v-9.9c0-1.8-1.2-2.9-2.9-2.9h-.6c-1.2 0-2.3-1.2-2.3-2.3v-59.6c0-1.2-1.2-2.9-2.9-2.9h-9.9c-1.2 0-2.9 1.2-2.9 2.9V180c0 4.7 4.7 9.9 9.9 9.9l8.7-.7zm68.9-56.7h-3.5v-1.8h9.9v1.8h-4.1v9.9h-2.3v-9.9zm8.2-1.8h2.9l1.8 4.1 1.2 2.9 1.2-2.9 1.8-4.1h2.9v11.7h-2.3V133l-2.9 7.6H893l-2.9-7.6v9.4h-1.8v-11.7z"
      />
    </svg>
  );
};

type LivebookingProps = {
  widgetUrl: string;
};

const Livebooking: React.FC<LivebookingProps> = ({ widgetUrl }) => (
  <iframe src={widgetUrl} className="h-[600px] w-full border-0"></iframe>
);

type QuandooProps = {
  quandooId: string;
};

// TODO: Move URL into a constant or config.
const Quandoo: React.FC<QuandooProps> = ({ quandooId }) => {
  return (
    <>
      <iframe
        src={`https://www.quandoo.de/checkout-widget/widget?agentId=6&merchantId=${quandooId}&primaryColor&theme=brand&widgetType=calendar`}
        width={'100%'}
        height={'600px'}
        className="border-0"
      />
      {/* <div id="quandoo-booking-widget" className="pt-6 mx-auto"></div>
      <script
        src="https://booking-widget.quandoo.com/index.js"
        data-agent-id="6"
        data-merchant-id={quandooId}
      ></script> */}
    </>
  );
};

type SpeisekarteTableBookingProps = {
  details: RestaurantInfoModalReservation_getRestaurantDetails;
  appContext: AppContext;
};

const SpeisekarteTableBooking: React.FC<SpeisekarteTableBookingProps> = ({
  details,
  appContext
}) => {
  const restaurantId = Number(details.id);
  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i.toString(),
    label: i.toString().padStart(2, '0')
  }));
  const minuteOptions = ['00', '15', '30', '45'].map(minute => ({
    value: minute,
    label: minute
  }));
  const personsOptions = Array.from({ length: 10 }, (_, i) => i + 1).map(i => ({
    value: i.toString(),
    label: `${i} ${i === 1 ? 'Person' : 'Personen'}`
  }));
  const phoneNumber = details.phone ? `${details.phoneprefix} / ${details.phone}` : '';

  const [firstStepData, setFirstStepData] = useState({
    date: '',
    time: '',
    numberOfPersons: 0
  });
  const [step, setStep] = useState(1); // TODO: Set to 1 instead of 2!
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const handleNext = () => setStep(step + 1);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setLoading(true);

    const formData = new FormData(event.currentTarget);
    const date = formData.get('tableBooking[date]') as string;
    const hour = formData.get('tableBooking[time][hour]');
    const minute = formData.get('tableBooking[time][minute]');
    const numberOfPersons = Number(formData.get('tableBooking[partySize]'));
    const dateTime = `${format(new Date(date), 'dd.MM.yyyy')} ${hour}:${minute}`;
    const firstName = formData.get('tableBooking[name][first]') as string;
    const lastName = formData.get('tableBooking[name][last]') as string;
    const email = formData.get('tableBooking[email]') as string;
    const phone = formData.get('tableBooking[phone]') as string;
    const annotation = formData.get('tableBooking[annotation]') as string;
    const privacyTermsAccepted = (formData.get('tableBooking[privacyTermsAccepted]') ==
      'on') as boolean;
    const bookingTermsAccepted = (formData.get('tableBooking[bookingTermsAccepted]') ==
      'on') as boolean;

    appContext.graphqlService = createGraphQLService(appContext, 'public');
    saveTableBooking(
      {
        restaurantId,
        dateTime,
        firstName,
        lastName,
        email,
        phone,
        numberOfPersons,
        annotation,
        userId: null,
        privacyTermsAccepted,
        bookingTermsAccepted,
        registerUser: false
      },
      appContext
    )
      .then(response => {
        setLoading(false);
        setError(response.data?.saveTableBooking !== true);
        handleNext();
      })
      .catch(() => {
        setLoading(false);
        setError(true);
        handleNext();
      });
  };

  const nextHour = (): string => {
    const currentHour = new Date().getHours();
    const nextHour = currentHour + 1;

    return nextHour >= 24 ? '0' : String(nextHour);
  };

  return (
    <>
      <form name="reservationForm" method="post" onSubmit={handleSubmit}>
        <div className={classList(step !== 1 && 'hidden')}>
          <div className="flex flex-col">
            <Datepicker
              name="tableBooking[date]"
              className="mt-4"
              min={new Date().toISOString().split('T')[0]}
              value={new Date().toISOString().split('T')[0]}
              required
            />
            <div className="mt-2 flex">
              <Dropdown
                options={hourOptions}
                name="tableBooking[time][hour]"
                selectedOption={nextHour()}
                containerClassName="flex-grow"
                required
              />
              <div className="p-2">:</div>
              <Dropdown
                options={minuteOptions}
                name="tableBooking[time][minute]"
                containerClassName="flex-grow"
                required
              />
            </div>
            <div className="mt-2">
              <Dropdown options={personsOptions} name="tableBooking[partySize]" required />
            </div>
          </div>

          <div className="mt-4">
            <Button
              className="w-full text-center"
              onClick={() => {
                handleNext();
                const win = safeWindow() as Window & {
                  reservationForm: HTMLFormElement | undefined;
                };
                if (win) {
                  const formData = new FormData(win.reservationForm);
                  const date = formData.get('tableBooking[date]') as string;
                  const hour = formData.get('tableBooking[time][hour]');
                  const minute = formData.get('tableBooking[time][minute]');
                  const numberOfPersons = Number(formData.get('tableBooking[partySize]'));

                  setFirstStepData({
                    date: format(date, 'dd.MM.yyyy'),
                    time: `${hour}:${minute}`,
                    numberOfPersons
                  });
                }
              }}
            >
              Jetzt anfragen
            </Button>
          </div>
        </div>

        <div className={classList(step !== 2 && 'hidden', 'mt-4')}>
          <div className="mb-2">
            Um Deine Reservierungsanfrage für {firstStepData.numberOfPersons}{' '}
            {pluralize(firstStepData.numberOfPersons, 'Person', 'Personen')} am {firstStepData.date}{' '}
            um {firstStepData.time} an "{details.name}" zu senden, fülle bitte folgende Felder aus:
          </div>
          <div className="flex flex-col gap-2">
            <Input label="Vorname *" name="tableBooking[name][first]" className="w-full" required />
            <Input label="Nachname *" name="tableBooking[name][last]" className="w-full" required />
            <Input label="E-Mail *" name="tableBooking[email]" className="w-full" required />
            <Input
              label="Handynummer *"
              name="tableBooking[phone]"
              type="text"
              className="w-full"
              inputMode="tel"
              required
            />
            <Textarea label="Anmerkungen" className="w-full" />
            <Label>
              <Checkbox
                label=""
                name="tableBooking[registerUser]"
                checked={false}
                onChange={console.log}
              />
              <p>
                Ja, ich möchte mich bei speisekarte.de registrieren und bestätige hiermit die{' '}
                <a
                  href="/nutzungsbedingungen"
                  className="text-sk-red-500 focus-visible:text-sk-red-900 hover:text-sk-red-900"
                >
                  Nutzungsbedingungen
                </a>
              </p>
            </Label>
            <Label>
              <Checkbox
                label=""
                name="tableBooking[privacyTermsAccepted]"
                checked={false}
                required={true}
                onChange={() => null}
              />
              <p>
                Hinweise zu Datenverarbeitung findest du in unserern{' '}
                <a
                  href="/datenschutz"
                  className="text-sk-red-500 focus-visible:text-sk-red-900 hover:text-sk-red-900"
                >
                  Datenschutzbestimmungen
                </a>{' '}
                *
              </p>
            </Label>
            <Label>
              <Checkbox
                label=""
                name="tableBooking[bookingTermsAccepted]"
                checked={false}
                required={true}
                onChange={() => null}
              />
              <p>
                Es gelten unsere{' '}
                <a
                  href="/nutzungsbedingungen-reservierung"
                  className="text-sk-red-500 focus-visible:text-sk-red-900 hover:text-sk-red-900"
                >
                  Reservierbedingungen
                </a>{' '}
                *
              </p>
            </Label>
          </div>

          <div className="mt-4">
            {loading ? (
              <Skeleton className="h-8 w-full rounded-full" />
            ) : (
              <Button className="w-full text-center" type="submit">
                Jetzt anfragen
              </Button>
            )}
          </div>
        </div>

        <div className={classList(step !== 3 && 'hidden', 'mt-4')}>
          {error ? (
            <>
              <p>Servus, leider ist etwas schief gelaufen. Bitte versuche es erneut.</p>
              <Button className="w-full text-center" onClick={() => setStep(1)}>
                Erneut versuchen
              </Button>
            </>
          ) : (
            <p>Vielen Dank für deine Reservierung.</p>
          )}
        </div>
      </form>

      {phoneNumber && step !== 3 && (
        <p className="mt-6">
          Willst Du für mehr als 10 Personen reservieren? Dann melde Dich direkt im Restaurant
          unter: <PhoneLink phone={phoneNumber} />
        </p>
      )}

      {step === 2 && <p className="mt-4 text-xs">* Pflichtfelder</p>}
    </>
  );
};
